<template>
    <ProfileView
        :title="$t('my_profile.title')"
        :subtitle="$t('my_profile.subtitle')"
        :loading="loading"
        :user="user"
        :stats="profile && profile.stats"
        :notification-preferences="profile && profile.notification_preferences ? profile.notification_preferences : []"
        :histories="profile && profile.histories"
    >
        <template #actions>
            <button v-if="!editing" class="mp-btn mp-btn--edit" @click="startEditing">
                <i class="fal fa-pen"></i>
                {{ $t('common.edit') }}
            </button>
        </template>

        <template #edit-panel>
            <section v-if="editing" class="mp-card">
                <header class="mp-card-header">
                    <i class="fal fa-pen mp-card-header-icon"></i>
                    <h3 class="mp-card-title">{{ $t('my_profile.personal_info') }}</h3>
                </header>
                <div class="mp-form">
                    <div class="mp-form-field">
                        <label>{{ $t('my_profile.name') }}</label>
                        <input type="text" v-model="form.name" />
                    </div>
                    <div class="mp-form-field">
                        <label>{{ $t('my_profile.email') }}</label>
                        <input type="email" v-model="form.email" />
                    </div>
                    <div class="mp-form-field">
                        <label>{{ $t('my_profile.phone') }}</label>
                        <input type="text" v-model="form.phone" placeholder="+33612345678" />
                    </div>
                    <div class="mp-form-grid">
                        <div class="mp-form-field">
                            <label>{{ $t('my_profile.job_title') }}</label>
                            <input type="text" v-model="form.job_title" />
                        </div>
                        <div class="mp-form-field">
                            <label>{{ $t('my_profile.language') }}</label>
                            <select v-model="form.locale">
                                <option value="fr">Français</option>
                                <option value="en">English</option>
                            </select>
                        </div>
                    </div>

                    <hr class="mp-divider" />

                    <h4 class="mp-form-section">{{ $t('my_profile.change_password') }}</h4>
                    <p class="mp-form-hint">{{ $t('my_profile.password_hint') }}</p>
                    <div class="mp-form-grid">
                        <div class="mp-form-field">
                            <label>{{ $t('my_profile.new_password') }}</label>
                            <input type="password" v-model="form.password" :placeholder="$t('my_profile.new_password')" />
                        </div>
                        <div class="mp-form-field">
                            <label>{{ $t('my_profile.confirm_password') }}</label>
                            <input type="password" v-model="form.password_confirmation" :placeholder="$t('my_profile.confirm_password')" />
                        </div>
                    </div>

                    <div class="mp-form-actions">
                        <button class="mp-btn mp-btn--ghost" @click="cancelEditing">
                            {{ $t('common.cancel') }}
                        </button>
                        <button class="mp-btn mp-btn--save" @click="saveProfile" :disabled="saving">
                            <i v-if="saving" class="fas fa-circle-notch fa-spin"></i>
                            <i v-else class="fal fa-check"></i>
                            {{ $t('common.save') }}
                        </button>
                    </div>
                </div>
            </section>
        </template>
    </ProfileView>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import ProfileView from '@/components/UI/ProfileView.vue'

export default {
    name: 'MyProfile',
    components: { ProfileView },
    data() {
        return {
            loading: true,
            editing: false,
            saving: false,
            profile: null,
            form: {
                name: '',
                email: '',
                phone: '',
                job_title: '',
                locale: '',
                password: '',
                password_confirmation: ''
            }
        }
    },
    computed: {
        ...mapState('auth', {
            user: (state) => state.user
        })
    },
    created() {
        this.getUserProfile(this.user.id)
            .then((data) => {
                this.profile = data
            })
            .catch(() => {})
            .finally(() => {
                this.loading = false
            })
    },
    methods: {
        ...mapActions('company', ['getUserProfile', 'updateUser']),
        startEditing() {
            this.form.name = this.user.name || ''
            this.form.email = this.user.email || ''
            this.form.phone = this.user.phone || ''
            this.form.job_title = this.user.job_title || ''
            this.form.locale = this.user.locale || 'fr'
            this.form.password = ''
            this.form.password_confirmation = ''
            this.editing = true
        },
        cancelEditing() {
            this.editing = false
        },
        saveProfile() {
            this.saving = true
            const payload = {
                id: this.user.id,
                name: this.form.name,
                email: this.form.email,
                phone: this.form.phone,
                job_title: this.form.job_title,
                locale: this.form.locale
            }
            if (this.form.password) {
                payload.password = this.form.password
                payload.password_confirmation = this.form.password_confirmation
            }
            this.updateUser(payload)
                .then(() => {
                    this.user.name = payload.name
                    this.user.email = payload.email
                    this.user.phone = payload.phone
                    this.user.job_title = payload.job_title
                    this.user.locale = payload.locale
                    localStorage.setItem('user', JSON.stringify(this.user))
                    if (payload.locale !== this.$i18n.locale) {
                        this.$store.commit('setLang', payload.locale)
                    }
                    this.editing = false
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('my_profile.saved')
                    })
                })
                .catch((errors) => {
                    this.$store.dispatch('toast', {
                        type: 'error',
                        title: Object.values(errors).flat().join(', ')
                    })
                })
                .finally(() => {
                    this.saving = false
                })
        }
    }
}
</script>

<style scoped>
.mp-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    white-space: nowrap;
    font-family: inherit;
}

.mp-btn i {
    font-size: 0.8rem;
}

.mp-btn--edit {
    background: var(--v2-primary);
    color: #fff !important;
}

.mp-btn--edit i {
    color: #fff !important;
}

.mp-btn--edit:hover {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.mp-btn--save {
    background: var(--v2-success);
    color: #fff !important;
}

.mp-btn--save i {
    color: #fff !important;
}

.mp-btn--save:hover:not(:disabled) {
    background: #059669;
    box-shadow: var(--v2-shadow-sm);
}

.mp-btn--save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.mp-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.mp-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.mp-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    margin-top: var(--v2-space-4);
}

.mp-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.mp-card-header-icon {
    color: var(--v2-primary);
    font-size: 0.95rem;
}

.mp-card-title {
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    margin: 0;
}

.mp-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.mp-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.mp-form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.mp-form-field label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.mp-form-field input,
.mp-form-field select {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
    font-family: inherit;
}

.mp-form-field input:focus,
.mp-form-field select:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.mp-divider {
    border: none;
    border-top: 1px solid var(--v2-border);
    margin: 6px 0;
}

.mp-form-section {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    margin: 0;
}

.mp-form-hint {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    margin: -10px 0 4px;
}

.mp-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 6px;
}

@media (max-width: 768px) {
    .mp-form-grid {
        grid-template-columns: 1fr;
    }
}
</style>
